import React, {useEffect, useState} from 'react';
import {Col, Row, Button, Nav, NavItem, NavLink as TabLink} from 'reactstrap';
import {useDispatch, useSelector} from 'react-redux';
import {NavLink, Outlet, useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {
    deleteOrganization,
    fetchOrganization,
    fetchOrganizationUsers,
    updateOrganization,
} from '../../slices/organizations.slice';
import TierBadge from '../../components/TierBadge';
import Avatar from '../../components/Avatar';
import EditModal from '../../components/EditModal';
import MetaChip from '../../components/MetaChip';
import {getCurrencyInfo} from '../../utils/currencies';
import {organizationSiteUrl} from '../../utils/organization';
import {countryName} from '../../utils/countries';
import './OrganizationDetails.scss'

// Site users get a condensed view: Overview folds in the cinema, systems and
// membership details, so only Overview and Users are shown.
const TABS = [
    { path: 'overview', label: 'Overview', icon: 'fa-building' },
    { path: 'users', label: 'Users', icon: 'fa-users' },
    { path: 'systems', label: 'Systems', icon: 'fa-server', bacOnly: true },
    { path: 'cinema', label: 'Cinema', icon: 'fa-video', bacOnly: true },
    { path: 'notes', label: 'Notes', icon: 'fa-sticky-note', bacOnly: true },
    { path: 'membership', label: 'Membership', icon: 'fa-book-open', bacOnly: true },
    { path: 'homepage', label: 'Homepage', icon: 'fa-clapperboard', bacOnly: true },
];

const OrganizationDetails = () => {
    const { organizationId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const token = useSelector(state => state.auth.token);
    const user = useSelector(state => state.auth.user);
    const organization = useSelector((state) => state.organizations.organization);
    const status = useSelector((state) => state.organizations.organizationStatus);
    const organizationUsers = useSelector((state) => state.organizations.organizationUsers);
    const orgUserStatus = useSelector((state) => state.organizations.organizationUsersStatus);
    const [logoModal, setLogoModal] = useState(false);

    const bacView = ['bac-admin', 'bac-user', 'root'].includes(user?.role);
    const siteUrl = organizationSiteUrl(organization);

    const handleUpdate = field => value => {
        const organizationBody = { ...organization, [field]: value };
        const orgId = organization._id;
        const promise = dispatch(updateOrganization({ token, organizationBody, orgId })).unwrap();
        toast.promise(promise, {
            pending: 'Updating...',
            success: 'Changes saved',
            error: 'Error updating changes',
        });
        return promise.then(() => dispatch(fetchOrganization({ token, orgId })));
    };

    const handleDelete = async () => {
        const promise = dispatch(deleteOrganization(organizationId)).unwrap();
        toast.promise(promise, {
            pending: 'Deleting organization',
            success: 'Organization deleted successfully',
            error: 'Error deleting organization',
        });
        await promise;
        navigate('/organizations');
    };

    useEffect(() => {
        if (!user) return;
        const orgId = organizationId === 'own' ? user.parentOrganization?._id : organizationId;
        dispatch(fetchOrganization({ token, orgId }));
        dispatch(fetchOrganizationUsers({ token, orgId }));
    }, [token, dispatch, organizationId, user]);

    const tabs = TABS.filter(tab => bacView || !tab.bacOnly);

    return status === 'success' && (
        <div className="organization-view p-3 ps-4 ms-1">
            {bacView && (
                <Row>
                    <Col>
                        <NavLink to="/organizations" className="text-decoration-none" >
                            <span className="fas fa-chevron-left me-2" /> Organizations
                        </NavLink>
                    </Col>
                </Row>
            )}

            <Row className="header border-start border-primary bg-broken" xs="4">
                <Col className="pic-container" xs="3" sm="auto">
                    <Avatar
                        imageUrl={organization.logo?.imageUrl}
                        name={organization.name}
                        size={100}
                        onEdit={() => setLogoModal(!logoModal)}
                    />
                </Col>
                <Col className="header-content flex-grow-1">
                    <div className="d-flex align-items-start justify-content-between gap-3">
                        <h1 className="name text-capitalize text-primary mb-0">
                            {organization.name}
                        </h1>

                        {organization.lastSynced && (
                            <span className="last-synced text-nowrap">
                                <span className="fas fa-refresh me-2 text-primary"/>
                                Last synced: {new Date(organization.lastSynced).toLocaleString()}
                            </span>
                        )}
                    </div>

                    {/* Metadata reads left to right, actions are pushed to the far
                        right so they never get mistaken for another chip. */}
                    <div className="meta-row d-flex align-items-center flex-wrap gap-2 mt-3">
                        <TierBadge tier={organization.tier}/>

                        {!user.role.startsWith('site-') && organization.currency && (
                            <MetaChip icon="fa-sack-dollar" label="Currency">
                                {organization.currency} - {getCurrencyInfo(organization.currency).label}
                            </MetaChip>
                        )}

                        {organization.country && (
                            <MetaChip icon="fa-map-marker-alt" label="Territory">
                                {countryName(organization.country)}
                            </MetaChip>
                        )}

                        {organization.siteLocation && (
                            <MetaChip icon="fa-globe" label="Site Location">
                                {countryName(organization.siteLocation)}
                            </MetaChip>
                        )}

                        <div className="header-actions d-flex gap-2 ms-auto">
                            {siteUrl && (
                                <a
                                    href={siteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={siteUrl}
                                    className="btn btn-outline-primary"
                                >
                                    <span className="fas fa-external-link-alt me-2"/>
                                    Open site
                                </a>
                            )}

                            {bacView && (
                                <NavLink to={`/organizations/${organizationId}/edit`}>
                                    <Button color="primary">
                                        <span className="fas fa-pencil me-2"/>
                                        Edit
                                    </Button>
                                </NavLink>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            <Nav tabs className="org-tabs mb-4">
                {tabs.map(({ path, label, icon }) => (
                    <NavItem key={path}>
                        <TabLink tag={NavLink} to={path} className="text-decoration-none">
                            <span className={`fas fa-fw ${icon} me-2`}/>
                            {label}
                        </TabLink>
                    </NavItem>
                ))}
            </Nav>

            <div className="details">
                <Outlet context={{
                    organization,
                    organizationUsers,
                    orgUserStatus,
                    user,
                    bacView,
                    handleUpdate,
                    handleDelete,
                }} />
            </div>

            <EditModal
                isOpen={logoModal}
                toggle={() => setLogoModal(!logoModal)}
                dataType="image"
                label="Organization logo"
                value={organization.logo?.imageUrl}
                onSave={({_id}) => handleUpdate('logo')(_id).then(() => setLogoModal(false))}
                mediaType="org-avatar"
            />
        </div>
    );
};

export default OrganizationDetails;
